import { useState, useEffect, useCallback, useRef } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';

interface ProfileCompletionState {
  needsProfileCompletion: boolean;
  isLoading: boolean;
  institution: string | null;
  role: string | null;
  checkProfile: () => Promise<boolean>;
}

export const useProfileCompletion = (): ProfileCompletionState => {
  const { user } = useAuth();
  const [needsProfileCompletion, setNeedsProfileCompletion] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [institution, setInstitution] = useState<string | null>(null);
  const [role, setRole] = useState<string | null>(null);
  
  // Use ref to track user ID without causing callback recreation
  const userIdRef = useRef<string | null>(null);

  // Keep userIdRef in sync with current user
  useEffect(() => {
    userIdRef.current = user?.id ?? null;
  }, [user?.id]);

  const checkProfile = useCallback(async (): Promise<boolean> => {
    const userId = userIdRef.current;
    
    if (!userId) {
      setNeedsProfileCompletion(false);
      setIsLoading(false);
      return false;
    }

    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('users')
        .select('institution, role')
        .eq('id', userId)
        .single();

      if (error) {
        // User profile doesn't exist yet - needs completion
        setNeedsProfileCompletion(true);
        setIsLoading(false);
        return true;
      }

      const isIncomplete = 
        !data.institution || 
        data.institution === 'Unknown';

      setInstitution(data.institution);
      setRole(data.role);
      setNeedsProfileCompletion(isIncomplete);
      setIsLoading(false);
      return isIncomplete;
    } catch (error) {
      console.error('Error checking profile:', error);
      setNeedsProfileCompletion(false);
      setIsLoading(false);
      return false;
    }
  }, []); // No dependencies - callback is stable

  // Run checkProfile when user ID changes
  useEffect(() => {
    checkProfile();
  }, [user?.id, checkProfile]);

  return {
    needsProfileCompletion,
    isLoading,
    institution,
    role,
    checkProfile,
  };
};
